import useDataDetail from "@/hooks/useDataDetail";
import SText from "@/styles/SText";
import { Bom, BomItem } from "@/types/bom";
import apis from "@/apis/bom";
import { Button, Flex, Modal, ModalProps, Table, TableColumnsType, TableProps } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const TableWrapper = styled.div`
  width: 100%;
  height: 100%;

  /* 隐藏滚动条 */
  .ant-table-body::-webkit-scrollbar {
    display: none;
  }
  .ant-table-body {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

interface BomDetailProps extends ModalProps {
  id: string;
}

export const BomDetail: FC<BomDetailProps> = ({ id, ...props }) => {

  const { t } = useTranslation();
  const { loading: deLoading, fetchDetail: fetch, detail } = useDataDetail<Bom, [string]>({ api: apis.fetch })

  const tableRef = useRef<HTMLDivElement>(null);
  const [expandIds, setExpandIds] = useState<Array<string>>([])
  const [tableHeight, setTableHeight] = useState<number>(300)

  // 递归获取所有可展开的行 ID
  const getAllExpandableIds = (items: BomItem[]): string[] => {
    const ids: string[] = []
    const traverse = (list: BomItem[]) => {
      for (const item of list) {
        if (item.type === 'formal' && item.subs && item.subs.length > 0) {
          ids.push(item.id)
          traverse(item.subs)
        }
      }
    }
    traverse(items)
    return ids
  }

  const allExpandableIds = useMemo(() => {
    return detail?.items ? getAllExpandableIds(detail.items) : []
  }, [detail])

  const handleExpandAll = () => {
    setExpandIds(allExpandableIds)
  }

  const handleCollapseAll = () => {
    setExpandIds([])
  }

  useEffect(() => {
    fetch(id)
  }, [])

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef?.current?.clientHeight - 40)
    }
  }, [tableRef.current])

  const columns: TableColumnsType<BomItem> = [
    { title: <SText text={t('bom.itemLevel')} />, dataIndex: 'level', key: 'level', width: 100, render: (info) => t('bom.itemLevelInfo', { level: info - 1 }) },
    { title: <SText text='Lev' />, dataIndex: 'level', key: 'levelCode', width: 80, render: (_, record) => record?.type === 'formal' ? Array(record.level).fill('+') : t('bom.itemReq') },
    { title: <SText text={t('bom.itemCode')} />, key: 'productCode', width: 100, render: (_, record) => record?.product?.code ?? '' },
    { title: <SText text={t('bom.itemName')} />, key: 'productName', width: 100, render: (_, record) => <SText text={record?.product?.name ?? ''} /> },
    { title: <SText text={t('bom.itemModal')} />, key: 'productModal', width: 240, render: (_, record) => <SText text={record?.product?.model?.toLocaleUpperCase() ?? ''} /> },
    { title: <SText text={t('bom.itemUnit')} />, key: 'productUom', width: 60, render: (_, record) => record?.product?.uom?.toLocaleUpperCase() ?? '' },
    { title: <SText text={t('bom.itemBase')} />, key: 'itemBase', width: 80, render: (_, record) => record?.baseUnit ?? '' },
    { title: <SText text={t('product.process')} />, key: 'process', width: 80, render: (_, record) => record?.process ?? '' },
    { title: <SText text={t('bom.itemExtraRate')} />, key: 'itemExtraRate', width: 80, render: (_, record) => (record?.extraRate ?? '') + '%' },
    { title: <SText text={t('bom.itemBaseQuantity')} />, key: 'itemBaseQuantity', width: 80, render: (_, record) => record?.baseQuantity ?? '' },
    { title: <SText text={t('bom.itemAttritionRate')} />, key: 'itemAttritionRate', width: 80, render: (_, record) => record?.attritionRate ?? '' },
    { title: <SText text={t('bom.itemUseQuantity')} />, key: 'itemUseQuantity', width: 80, render: (_, record) => record?.useQuantity ?? '' },
    { title: <SText text={t('bom.itemEffectiveDate')} />, key: 'startDate', width: 80, render: (_, record) => record?.startDate ?? '' },
    { title: <SText text={t('bom.itemExpiringDate')} />, key: 'endDate', width: 80, render: (_, record) => record?.endDate ?? '' },
    { title: <SText text={t('bom.itemReMark')} />, key: 'itemReMark', width: 80, render: (_, record) => record?.remark ?? '' },
    { title: <SText text={t('bom.itemLocators')} />, key: 'itemLocators', width: 80, render: (_, record) => record?.locators ?? '' },
  ];

  const expandedRowRender = (record: BomItem) => (
    <Table<BomItem>
      columns={columns}
      dataSource={record.subs}
      sticky
      scroll={{ x: 1400, y: tableHeight }}
      {...tableProps}
    />
  );

  const tableProps: TableProps<BomItem> = {
    rowKey: 'id',
    expandable: {
      expandedRowRender,
      rowExpandable: record => record.type === 'formal' && record.subs.length > 0,
      expandedRowKeys: expandIds,
      onExpand: (expanded, record) => {
        if (expanded) {
          setExpandIds([...expandIds, record.id])
        } else {
          setExpandIds(prev => prev.filter(item => item !== record.id))
        }
      },
    },
    pagination: false,
    size: "small"
  }

  return (
    <Modal {...props} title={t('bom.bomDetail')} width='90vw' loading={deLoading} centered
      footer={null}
      styles={{
        body: {
          height: '60vh',
        }
      }}>
      <Flex vertical style={{ width: "100%", height: "100%" }}>
        <Flex gap={8} style={{ marginBottom: 8 }}>
          <Button type="primary" onClick={handleExpandAll}>{t('bom.expandAll')}</Button>
          <Button onClick={handleCollapseAll}>{t('bom.collapseAll')}</Button>
        </Flex>
        <TableWrapper ref={tableRef}>
          <Table<BomItem>
            columns={columns}
            dataSource={detail?.items?.sort((a, b) => a.index - b.index)}
            scroll={{ x: 1400, y: tableHeight }}
            {...tableProps}
          />
        </TableWrapper>
      </Flex>
    </Modal>
  )
}